import React from "react";
import Link from "next/link";
import router from "next/router";
import { useMachine } from "@xstate/react";
import { useGroups } from "@nju/data-access-portal-dashboard";
import { IMobileOffer, useOffer } from "@nju/data-access-portal-offers";
import { onboardingInProgress } from "@nju/data-access-portal-orchestrator";
import { useUserLoginState } from "@nju/data-access-portal-user";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import { A, BlockText, Heading, Separator, Text } from "@nju/ui";
import { formatPrice } from "@nju/utils";
import { subscribeOfferDetailsMachine } from "./subscribeOfferDetails.machine";

interface ISubscribeOfferDetailsProps {
  type: "lp" | "dashboard";
  mobileOffers: IMobileOffer[];
}

export function SubscribeOfferDetails({
  type,
  mobileOffers,
}: ISubscribeOfferDetailsProps) {
  const cheapestFtthOffer = useOffer("P-NJU-Fiber-200-v2");
  const groups = useGroups();
  const { isLoggedIn } = useUserLoginState();
  const lpRedirectWhenUserIsNotLoggedIn =
    "https://www.njumobile.pl/oferta/abonament-mono";
  const lpRedirectWhenUserIsLoggedIn = portalRoutes.wybierak.get();
  const offer = mobileOffers.find((offer) => {
    return offer.display;
  });

  const [, send] = useMachine(subscribeOfferDetailsMachine, {
    actions: {
      onSubmit: (_, event) => {
        if (event.type !== "SUCCESS") {
          return;
        }

        if (
          event.orchestrator &&
          onboardingInProgress(
            event.orchestrator?.state,
            event.orchestrator?.type,
          )
        ) {
          return router.push(
            portalRoutes.mojeKonto.index.get({
              mode: "mobile",
            }),
          );
        }

        return router.push(portalRoutes.wybierak.get());
      },
      onErrorRedirect: () => {
        return router.push(portalRoutes.zamowienieWTrakcie.get());
      },
    },
  });

  const handleSubmit = React.useCallback(() => {
    send({ type: "SUBMIT" });
  }, [send]);

  return (
    <>
      <Separator size="s" tone="invisible" />
      <Heading
        align="left"
        className="pr-2"
        font="lato"
        tone="accent"
        variant="h2"
      >
        W PAKIECIE TANIEJ
      </Heading>
      <div className="flex gap-4 mt-5 p-[16px] rounded-[12px] bg-ripeLemon items-center">
        <div>
          <Icon height={22} name="badge-percent" width={22} />
        </div>
        <Text
          className="pt-smallgap from-tablet:max-w-[400px]"
          size="s"
          weight="bold"
        >
          rabat {offer?.ftthBundleDiscount || offer?.bundleDiscount} zł/mies. na
          jeden nju subskrypcja, gdy kupisz obie usługi - nju światłowód i nju
          subskrypcja
        </Text>
      </div>
      <Separator size="s" tone="invisible" />
      <BlockText
        className="pb-4"
        size={{
          "@onlyMobile": "m",
          "@fromDesktop": "xl",
        }}
        weight="bold"
      >
        jak stworzyć pakiet?
      </BlockText>
      <ul className="pl-3 list-disc marker:text-cerulean text-size-14">
        <li className="pl-2">
          Kup nju światłowód i nju subskrypcja (każdą z usług oddzielnie).
        </li>
        <Separator size="xs" tone="invisible" />
        <li className="pl-2">
          Jeśli masz już aktywną jedną usługę, możesz dokupić drugą.
        </li>
        <Separator size="xs" tone="invisible" />
        <li className="pl-2">Kolejność zakupu jest dowolna.</li>
      </ul>
      <Separator size="xs" tone="invisible" />
      <BlockText
        className="pt-3"
        size={{
          "@onlyMobile": "m",
          "@fromDesktop": "xl",
        }}
        weight="bold"
      >
        sprawdź dostępność i kup nju światłowód
      </BlockText>
      <Separator
        size={{
          "@onlyMobile": "s",
          "@fromDesktop": "xs",
        }}
        tone="invisible"
      />
      <div
        className="flex flex-wrap gap-4 pb-6 max-w-[1200px] w-full z-[1]"
        data-cy="offer-details-boxes"
      >
        <div
          className="flex items-center justify-between bg-white rounded-[12px] p-5 w-full h-[100px] relative shadow-[0px_2px_16px_rgba(0,39,63,0.12)]"
          data-cy="ftth-offer-details-box"
        >
          {typeof cheapestFtthOffer?.totalRecurringPrice === "number" && (
            <div className="flex items-center gap-4">
              <Icon height={40} name="globe" tone="accent" width={40} />
              <div className="flex flex-col">
                <Text display="block" size="l" weight="bold">
                  nju światłowód
                </Text>
                <Text display="block" size="s" weight="bold">
                  od {formatPrice(cheapestFtthOffer.totalRecurringPrice, false)}{" "}
                  zł/mies.
                </Text>
              </div>
            </div>
          )}
          <div>
            <Link href={portalRoutes.swiatlowod.get()} passHref={true}>
              <A className="flex justify-start" size="inline" variant="neutral">
                <div className="flex items-center gap-2 cursor-pointer">
                  <Text
                    testId="check-ftth-offer-button-in-details-offer-window"
                    tone="accent"
                  >
                    sprawdź dostępność
                  </Text>
                  <Icon
                    height={19}
                    name="arrow-right"
                    tone="accent"
                    width={19}
                  />
                </div>
              </A>
            </Link>
          </div>
        </div>
        <BlockText
          className="pt-2"
          size={{
            "@onlyMobile": "m",
            "@fromDesktop": "xl",
          }}
          weight="bold"
        >
          kup nju subskrypcja
        </BlockText>
        <div
          className="flex justify-between bg-white rounded-[12px] p-5 w-full h-[100px] relative shadow-[0px_2px_16px_rgba(0,39,63,0.12)]"
          data-cy="mv-offer-details-box"
        >
          <div className="flex items-center gap-4">
            <Icon height={40} name="mobile" tone="accent" width={40} />
            <div className="flex flex-col">
              <Text display="block" size="l" weight="bold">
                nju subskrypcja
              </Text>
              <Text display="block" size="s" weight="bold">
                od {offer?.priceDiscountedAfterFtth as number} zł/mies.
              </Text>
            </div>
          </div>
          {
            <>
              {type === "lp" && (
                <Link
                  href={
                    !isLoggedIn
                      ? lpRedirectWhenUserIsNotLoggedIn
                      : lpRedirectWhenUserIsLoggedIn
                  }
                  passHref={true}
                >
                  <A
                    className="flex justify-start"
                    size="inline"
                    variant="neutral"
                  >
                    <div className="flex items-center gap-2 cursor-pointer">
                      <Text
                        testId="check-mv-offer-button-in-details-offer-window"
                        tone="accent"
                      >
                        sprawdź
                      </Text>
                      <Icon
                        height={19}
                        name="arrow-right"
                        tone="accent"
                        width={19}
                      />
                    </div>
                  </A>
                </Link>
              )}
              {type === "dashboard" && (
                <>
                  {groups && groups?.[0]?.members?.length > 0 ? (
                    <Link
                      href={portalRoutes.mojeKonto.index.get({
                        mode: "mobile",
                      })}
                      passHref={true}
                    >
                      <A
                        className="flex justify-start"
                        size="inline"
                        variant="neutral"
                      >
                        <div className="flex items-center gap-2 cursor-pointer">
                          <Text tone="accent">sprawdź</Text>
                          <Icon
                            height={19}
                            name="arrow-right"
                            tone="accent"
                            width={19}
                          />
                        </div>
                      </A>
                    </Link>
                  ) : (
                    <div
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={handleSubmit}
                    >
                      <Text tone="accent">sprawdź</Text>
                      <Icon
                        height={19}
                        name="arrow-right"
                        tone="accent"
                        width={19}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          }
        </div>
        <BlockText
          className="pt-2"
          size={{
            "@onlyMobile": "m",
            "@fromDesktop": "xl",
          }}
          weight="bold"
        >
          masz dwie usługi - masz pakiet
        </BlockText>
        <div className="flex items-center gap-4 py-5 px-[16px] rounded-[12px] bg-ripeLemon">
          <div className="flex gap-2">
            <Icon height={30} name="globe" width={30} />
            <Icon height={30} name="mobile" width={30} />
          </div>
          <Text className="pt-smallgap">
            rabat {offer?.ftthBundleDiscount || offer?.bundleDiscount} zł
            naliczamy automatycznie na opłacie miesięcznej za nju subskrypcja
          </Text>
        </div>
      </div>
    </>
  );
}
